'use client'
import Button from '@components/common/Button'
import Typography from '@components/common/Typography'
import Input, {Field} from '@components/common/Input'
import Email from '@svg/email.svg'
import {FormEvent, useEffect, useState} from 'react'
import {errorToast, successToast} from 'utils/Toast'
import {load as loadRecaptchaV3, ReCaptchaInstance} from 'recaptcha-v3'

function ContactUsForm({withTitle = true}: {withTitle?: boolean}) {
  const [errors, setErrors] = useState<string[]>([])
  const [success, setSuccess] = useState<boolean>(false)
  const [loading, setLoading] = useState(false)
  const [recaptchaInstance, setRecaptchaInstance] = useState<ReCaptchaInstance | null>(null)

  useEffect(() => {
    async function loadRecaptcha() {
      const instance = await loadRecaptchaV3(process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || '', {
        autoHideBadge: true,
      })
      setRecaptchaInstance(instance)
    }
    if (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY) {
      loadRecaptcha()
    }
  }, [])

  const fields: Field[] = [
    {
      id: 'firstName',
      model: 'firstName',
      placeholder: 'Name',
      required: true,
    },
    {
      id: 'email',
      model: 'email',
      type: 'email',
      placeholder: 'Email',
      required: true,
    },
    {
      id: 'website',
      model: 'website',
      type: 'website',
      placeholder: 'Website',
      required: true,
    },
    {
      id: 'message',
      model: 'message',
      type: 'textarea',
      placeholder: 'Message',
      required: true,
      rows: 5,
    },
  ]

  const handleOnSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    if (loading) return

    const formData = new FormData(e.currentTarget)
    const data = Object.fromEntries(formData)

    const requiredFields = fields.filter(field => field.required)
    const missingFields = requiredFields.filter(field => !data[field.model])
    if (missingFields.length > 0) {
      setErrors(missingFields.map(field => field.model))
      return
    }

    try {
      setLoading(true)

      let recaptcha = ''
      if (recaptchaInstance) {
        recaptcha = await recaptchaInstance.execute('form_submit')
      }

      formData.append('recaptcha', recaptcha)
      formData.append('func', 'contact')
      formData.append('toCmp', 'xPayz Market')
      const resp: Response = await fetch(process.env.NEXT_PUBLIC_API_URL ?? '/api', {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
      })
      const json = await resp.json()

      if (json.success) {
        setSuccess(true)
        successToast(json.message)
      } else {
        errorToast(json.message)
      }
    } catch (error) {
      errorToast('Something went wrong')
    }
    setLoading(false)

    // reset the form
    for (let i = 0; i < fields.length; i++) {
      ;(document.getElementById(fields[i].id) as HTMLInputElement).value = ''
    }
  }

  return (
    <div
      className={
        withTitle
          ? `grid grid-cols-1 md:grid-cols-2 w-300 max-w-full gap-6 items-start bg-linear-to-r from-white to-primary-50/20 border-4 border-primary-400 shadow-[0px_25px_50px_-12px_rgba(0,0,0,0.25)] rounded-3xl px-12 sm:px-14 md:px-16 lg:px-20 py-10 sm:py-12 md:py-14 lg:py-16`
          : `p-2`
      }>
      {withTitle && (
        <div className="flex flex-col gap-12 ">
          <div className="flex flex-col gap-4">
            <Typography size="xl">Contact Us</Typography>
            <Typography size="lg" className="max-w-100">
              We are committed to processing the information in order to contact you and talk about your project.
            </Typography>
          </div>
          <div className="flex gap-10 items-center">
            <Email className="w-5 h-5 text-orange-400" />
            <Typography size="md">info@xpayz.market</Typography>
          </div>
        </div>
      )}
      <form name="contact-us" onSubmit={handleOnSubmit}>
        <input type="hidden" name="form-name" value="contact-us" />
        <p className="hidden">
          <label>
            Don’t fill this out if you’re human: <input name="bot-field" type="text" />
          </label>
        </p>
        <div className="flex flex-col gap-4">
          {fields.map((field, index) => {
            return <Input key={index} field={field} />
          })}
        </div>
        <div className="flex w-full justify-center sm:justify-start mt-8">
          <Button variant="primary" type="submit" className="w-full" loading={loading}>
            Submit
          </Button>
        </div>
      </form>
    </div>
  )
}

export default ContactUsForm
